import type {
  GetProductListInput,
  GetProductListOutput,
  Product,
  ProductOutput,
  CreatePaymentIntentInput,
  UpdatePaymentIntentInput,
  PaymentIntentOutput,
  FindOrdersByEmailInput,
  FindOrdersByEmailOutput,
  FindOrderByShortOrderIdInput,
  OrderOutput,
  Order,
  CreateEmailNotificationInput,
  CreateEmailNotificationOutput
} from "@tempefab-model/tempe-fabrication-client";
import {
  GetProductListCommand,
  GetProductCommand,
  CreatePaymentIntentCommand,
  UpdatePaymentIntentCommand,
  CancelPaymentIntentCommand,
  FindOrdersByEmailCommand,
  FindOrderByShortOrderIdCommand,
  GetOrderCommand,
  CreateAttributionCommand,
  CreateEmailNotificationCommand
} from "@tempefab-model/tempe-fabrication-client";
import { TempeFabricationPublicServiceException } from "@tempefab-model/tempe-fabrication-client";
import { sessionClient as client } from "./tempeFabricationSessionClient";

/**
 * Address structure matching the Smithy Address type
 */
interface Address {
  streetAddress: string;
  apartmentUnit?: string;
  city: string;
  state: string;
  zipCode: string;
}

/**
 * Enhanced error object with structured information
 */
interface EnhancedError extends Error {
  type: string;
  productId?: string;
  fieldList?: Array<{ path: string; message: string }>;
  possibleAddresses?: Address[];
  originalError: any;
}

/**
 * Centralized error parsing logic for all Smithy service exceptions
 * Handles all possible error types from TempeFabricationPublicServiceException
 * @param error - The caught error object
 * @param context - Optional context string for logging (e.g., "creating payment intent")
 * @returns EnhancedError with user-friendly message and structured data
 */
function parseServiceError(error: any, context?: string): EnhancedError {
  const logPrefix = context ? `[TempeFabClientWrapper] Error ${context}:` : '[TempeFabClientWrapper] Error:';
  console.error(logPrefix, error);

  // Check if this looks like a Smithy error by examining its properties
  // Smithy errors typically have: name, __type, $metadata, or are instanceof ServiceException
  const isSmithyError = error instanceof TempeFabricationPublicServiceException ||
                        error?.name ||
                        error?.__type ||
                        error?.$metadata;

  if (isSmithyError) {
    const smithyError = error as any;

    // 1. ProductUnavailable exception - specific to payment operations
    // Check by name, __type, OR presence of productId field
    if (smithyError.name === 'ProductUnavailable' ||
        smithyError.__type === 'ProductUnavailable' ||
        (smithyError.productId !== undefined && smithyError.error)) {
      console.error(`${logPrefix} ProductUnavailable exception:`, {
        productId: smithyError.productId,
        error: smithyError.error
      });
      const enhancedError = new Error(smithyError.error || 'Product is unavailable') as EnhancedError;
      enhancedError.type = 'ProductUnavailable';
      enhancedError.productId = smithyError.productId;
      enhancedError.originalError = error;
      return enhancedError;
    }

    // 1b. InvalidAddressException - address validation failed with possible corrections
    // Check by name, __type, OR presence of possibleAddresses field
    if (smithyError.name === 'InvalidAddressException' ||
        smithyError.__type === 'InvalidAddressException' ||
        (smithyError.possibleAddresses !== undefined && smithyError.error)) {
      console.error(`${logPrefix} InvalidAddressException:`, {
        error: smithyError.error,
        possibleAddressesCount: smithyError.possibleAddresses?.length || 0
      });
      const enhancedError = new Error(smithyError.error || 'Invalid address') as EnhancedError;
      enhancedError.type = 'InvalidAddressException';
      enhancedError.possibleAddresses = smithyError.possibleAddresses || [];
      enhancedError.originalError = error;
      return enhancedError;
    }

    // 2. NoSuchResource exception - 422 error code
    // Used when resources (orders, products, etc.) are not found
    // Check by name, __type, OR by status code 422 with resource field
    if (smithyError.name === 'NoSuchResource' ||
        smithyError.__type === 'NoSuchResource' ||
        (smithyError.$metadata?.httpStatusCode === 422 && smithyError.resource) ||
        (smithyError.name === '422' && smithyError.resource)) {
      console.error(`${logPrefix} NoSuchResource exception`);
      // The actual error message is in the 'resource' field for 422 errors
      const errorMessage = smithyError.resource || smithyError.error || smithyError.message || 'Resource not found';
      const enhancedError = new Error(errorMessage) as EnhancedError;
      enhancedError.type = 'NoSuchResource';
      enhancedError.originalError = error;
      return enhancedError;
    }

    // 3. ValidationException - field-level validation errors
    if (smithyError.name === 'ValidationException' || smithyError.__type === 'ValidationException') {
      console.error(`${logPrefix} ValidationException:`, smithyError.fieldList);
      const fieldErrors = smithyError.fieldList?.map((f: any) => `${f.path}: ${f.message}`).join(', ') || 'Validation failed';
      const enhancedError = new Error(fieldErrors) as EnhancedError;
      enhancedError.type = 'ValidationException';
      enhancedError.fieldList = smithyError.fieldList;
      enhancedError.originalError = error;
      return enhancedError;
    }

    // 4. InvalidInput exception - general input validation errors
    if (smithyError.name === 'InvalidInput' || smithyError.__type === 'InvalidInput') {
      console.error(`${logPrefix} InvalidInput exception:`, smithyError.error);
      const enhancedError = new Error(smithyError.error || 'Invalid input provided') as EnhancedError;
      enhancedError.type = 'InvalidInput';
      enhancedError.originalError = error;
      return enhancedError;
    }

    // 5. Generic ServiceException with error field (string message)
    if (smithyError.error && typeof smithyError.error === 'string') {
      console.error(`${logPrefix} ServiceException with error field:`, smithyError.error);
      const enhancedError = new Error(smithyError.error) as EnhancedError;
      enhancedError.type = 'ServiceException';
      enhancedError.originalError = error;
      return enhancedError;
    }

    // 6. Generic ServiceException with message field
    if (smithyError.message) {
      console.error(`${logPrefix} ServiceException with message:`, smithyError.message);
      const enhancedError = new Error(smithyError.message) as EnhancedError;
      enhancedError.type = 'ServiceException';
      enhancedError.originalError = error;
      return enhancedError;
    }

    // 7. ServiceException without recognizable error field
    console.error(`${logPrefix} Unrecognized ServiceException structure:`, smithyError);
    const enhancedError = new Error('An unexpected error occurred') as EnhancedError;
    enhancedError.type = 'UnknownServiceException';
    enhancedError.originalError = error;
    return enhancedError;
  }

  // Non-ServiceException errors (network errors, etc.)
  console.error(`${logPrefix} Non-ServiceException error:`, error);
  const enhancedError = new Error(error?.message || 'An unexpected error occurred') as EnhancedError;
  enhancedError.type = 'UnknownError';
  enhancedError.originalError = error;
  return enhancedError;
}

/**
 * Service class for interacting with TempeFabrication API
 */
class TempeFabricationClientWrapper {
  /**
   * Get a paginated list of products
   * @param input - Optional pagination and filter parameters
   * @returns Promise with product list results
   */
  async getAllProducts(input?: GetProductListInput): Promise<GetProductListOutput> {
    try {
      const command = new GetProductListCommand(input || {});
      return await client.send(command);
    } catch (error) {
      throw parseServiceError(error, 'fetching products');
    }
  }

  /**
   * Get products with a specific filter
   * @param filter - Filter string (e.g., "name_contains=Bar&status=AVAILABLE")
   * @param limit - Maximum number of results
   * @returns Promise with filtered product list
   */
  async getProductsByFilter(filter: string, limit?: string): Promise<GetProductListOutput> {
    return this.getAllProducts({
      filter,
      limit: limit || "20"
    });
  }

  /**
   * Get products by category
   * @param category - Product category
   * @param limit - Maximum number of results
   * @returns Promise with filtered product list
   */
  async getProductsByCategory(category: string, limit?: string): Promise<GetProductListOutput> {
    return this.getProductsByFilter(`category=${category}`, limit);
  }

  /**
   * Get available products only
   * @param limit - Maximum number of results
   * @returns Promise with available products
   */
  async getAvailableProducts(limit?: string): Promise<GetProductListOutput> {
    return this.getProductsByFilter("status=AVAILABLE", limit);
  }

  /**
   * Get a single product by ID
   * @param id - Product ID
   * @returns Promise with product details
   */
  async getProduct(id: string): Promise<ProductOutput> {
    try {
      const command = new GetProductCommand({ id });
      return await client.send(command);
    } catch (error) {
      throw parseServiceError(error, 'fetching product');
    }
  }

  /**
   * Create a payment intent for processing an order
   * @param input - Payment intent details including customer and product information
   * @returns Promise with client secret for Stripe
   * @throws {Error} Parsed error with user-friendly message
   */
  async createPaymentIntent(input: CreatePaymentIntentInput): Promise<PaymentIntentOutput> {
    try {
      const command = new CreatePaymentIntentCommand(input);
      return await client.send(command);
    } catch (error) {
      throw parseServiceError(error, 'creating payment intent');
    }
  }

  /**
   * Update an existing payment intent
   * @param input - Payment intent update details including ID and updated information
   * @returns Promise with updated payment intent output
   * @throws {Error} Parsed error with user-friendly message
   */
  async updatePaymentIntent(input: UpdatePaymentIntentInput): Promise<PaymentIntentOutput> {
    try {
      const command = new UpdatePaymentIntentCommand(input);
      return await client.send(command);
    } catch (error) {
      throw parseServiceError(error, 'updating payment intent');
    }
  }

  /**
   * Cancel a payment intent (courtesy call to backend)
   * @param id - Payment intent ID
   * @returns Promise that resolves when cancel request completes (errors are logged but not thrown)
   */
  async cancelPaymentIntent(id: string): Promise<void> {
    try {
      const command = new CancelPaymentIntentCommand({ id });
      await client.send(command);
      console.log('[TempeFabClientWrapper] Payment intent cancelled successfully:', id);
    } catch (error) {
      // Log error but don't throw - this is a courtesy call
      console.error('[TempeFabClientWrapper] Error cancelling payment intent (non-critical):', error);
    }
  }

  /**
   * Find orders by email and shipping zip code
   * @param input - Email and shipping zip code
   * @returns Promise with array of matching orders
   * @throws {Error} Parsed error with user-friendly message
   */
  async findOrdersByEmail(input: FindOrdersByEmailInput): Promise<FindOrdersByEmailOutput> {
    try {
      const command = new FindOrdersByEmailCommand(input);
      return await client.send(command);
    } catch (error) {
      throw parseServiceError(error, 'finding orders by email');
    }
  }

  /**
   * Find a specific order by short order ID and email
   * @param input - Short order ID and email
   * @returns Promise with order details
   * @throws {Error} Parsed error with user-friendly message
   */
  async findOrderByShortOrderId(input: FindOrderByShortOrderIdInput): Promise<OrderOutput> {
    try {
      const command = new FindOrderByShortOrderIdCommand(input);
      return await client.send(command);
    } catch (error) {
      throw parseServiceError(error, 'finding order by short ID');
    }
  }

  /**
   * Get a specific order by ID (UUID)
   * @param id - Order UUID
   * @returns Promise with order details
   * @throws {Error} Parsed error with user-friendly message
   */
  async getOrder(id: string): Promise<OrderOutput> {
    try {
      const command = new GetOrderCommand({ id });
      return await client.send(command);
    } catch (error) {
      throw parseServiceError(error, 'getting order');
    }
  }

  /**
   * Track when a product is added to cart (fire-and-forget)
   * @param productId - The product ID being added
   * @param quantity - The quantity being added
   */
  trackProductAddedToCart(productId: string, quantity: number): void {
    const command = new CreateAttributionCommand({
      eventType: "PRODUCT_ADDED_TO_CART",
      products: [
        {
          productId,
          quantity,
        }
      ]
    });

    // Fire and forget - don't await, just log errors
    client.send(command).catch((error) => {
      console.error('[TempeFabClientWrapper] Error tracking product added to cart (non-critical):', error);
    });
  }

  /**
   * Send a contact form email notification
   * @param input - Contact form input (senderEmail, senderName, subject, message, optional phone and additionalInformation)
   * @returns Promise with the result
   * @throws {Error} Parsed error with user-friendly message
   */
  async sendContactFormEmail(input: CreateEmailNotificationInput): Promise<CreateEmailNotificationOutput> {
    try {
      const command = new CreateEmailNotificationCommand(input);
      return await client.send(command);
    } catch (error) {
      throw parseServiceError(error, 'sending contact form email');
    }
  }
}

// Export a singleton instance
export const tempeFabricationClientWrapper = new TempeFabricationClientWrapper();

// Export session management functions for attribution tracking
export { clearSessionId, getCurrentSessionId } from "./tempeFabricationSessionClient";

// Export types for use in components
export type {
  Product,
  GetProductListOutput,
  CreatePaymentIntentInput,
  UpdatePaymentIntentInput,
  PaymentIntentOutput,
  FindOrdersByEmailInput,
  FindOrdersByEmailOutput,
  FindOrderByShortOrderIdInput,
  OrderOutput,
  Order,
  Address,
  CreateEmailNotificationInput,
  CreateEmailNotificationOutput,
};
